import { articles as rawArticles, categories as rawCategories } from "@/mocks/news";
import type { Article, Category, SortType, TimeRange } from "@/types/news";

export const categories: Category[] = rawCategories;
export const articles: Article[] = rawArticles;

/** 演示站点的“当前时间”，保证筛选/排序结果稳定可复现 */
export const DEMO_NOW = new Date("2026-10-15T21:00:00+08:00").getTime();

const RANGE_MS: Record<TimeRange, number> = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
  all: Number.MAX_SAFE_INTEGER,
};

export const timeRangeOptions: { value: TimeRange; label: string }[] = [
  { value: "all", label: "不限时间" },
  { value: "24h", label: "最近 24 小时" },
  { value: "7d", label: "最近 7 天" },
  { value: "30d", label: "最近 30 天" },
];

export const sortOptions: { value: SortType; label: string }[] = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "热度排序" },
];

export function getCategory(key: string): Category | undefined {
  return categories.find((item) => item.key === key);
}

export function getArticleById(id: string): Article | undefined {
  return articles.find((item) => item.id === id);
}

export function sortArticles(list: Article[], sort: SortType): Article[] {
  const copy = [...list];
  if (sort === "hot") {
    return copy.sort((a, b) => b.views - a.views);
  }
  return copy.sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
}

export function filterByRange(list: Article[], range: TimeRange): Article[] {
  if (range === "all") return list;
  const limit = RANGE_MS[range];
  return list.filter((item) => {
    const t = new Date(item.publishedAt).getTime();
    return DEMO_NOW - t <= limit && t <= DEMO_NOW;
  });
}

export function latestArticles(limit?: number): Article[] {
  const sorted = sortArticles(articles, "latest");
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function hotArticles(limit?: number): Article[] {
  const sorted = sortArticles(articles, "hot");
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getArticlesByCategory(
  categoryKey: string,
  options: { range?: TimeRange; sort?: SortType } = {},
): Article[] {
  const { range = "all", sort = "latest" } = options;
  const inCategory = articles.filter((item) => item.category === categoryKey);
  return sortArticles(filterByRange(inCategory, range), sort);
}

export function searchArticles(keyword: string): Article[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return articles.filter((item) => {
    const haystack = [
      item.title,
      item.subtitle,
      item.summary,
      item.content,
      item.author,
      ...item.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function getRelatedArticles(current: Article, limit = 5): Article[] {
  const sameCategory = sortArticles(
    articles.filter(
      (item) => item.category === current.category && item.id !== current.id,
    ),
    "hot",
  );
  if (sameCategory.length >= limit) return sameCategory.slice(0, limit);
  const others = sortArticles(
    articles.filter(
      (item) => item.category !== current.category && item.id !== current.id,
    ),
    "hot",
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export function formatDate(iso: string, withTime = false): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  if (!withTime) return `${y}-${m}-${day}`;
  const hh = `${d.getHours()}`.padStart(2, "0");
  const mm = `${d.getMinutes()}`.padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

export function relativeTime(iso: string): string {
  const diff = DEMO_NOW - new Date(iso).getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < hour) return `${Math.max(1, Math.floor(diff / minute))} 分钟前`;
  if (diff < day) return `${Math.floor(diff / hour)} 小时前`;
  if (diff < 30 * day) return `${Math.floor(diff / day)} 天前`;
  return formatDate(iso);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
}

export function splitParagraphs(content: string): string[] {
  return content
    .split("\n\n")
    .map((p) => p.trim())
    .filter(Boolean);
}